import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { Button } from 'common/components/Buttons/Button';
import { Dialog } from 'common/components/Dialog/Dialog';
import { useMutation, useSuspenseQuery } from '@tanstack/react-query';

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import ConfigurationIcon from 'assets/images/configuration.svg';
import PendingIcon from 'assets/images/pending.svg';

import MenuItem from '@mui/material/MenuItem';

import Grid from '@mui/material/Grid';
import { Actions, Subjects } from 'permissions/ability';
import { SimInventoryCan } from 'permissions/PermissionProvider';
import { useAbility } from 'permissions/hooks/useAbility';
import { PossibleConfigurationToRevert } from 'rSimInventory/RSimApi/rSimConfigurationApi.types';
import {
  fetchRSimCurrentAppliedConfigurationDetails,
  fetchRSimPossibleOTAConfigurationsToRevert,
  revertConfigurationVersion,
} from 'rSimInventory/RSimApi/rSimConfigurationApi';
import { Select } from 'common/components/Inputs/Select/Select';
import { Tooltip } from 'common/components/Tooltip/Tooltip';
import { AutoHideSnackbar } from 'common/Snackbar/AutoHideSnackbar';
import { AlertTitle } from 'common/Snackbar/AlertTitle';
import { pxToRem } from 'src/common/utils/fonts';

type RestoreStatuses = { success: boolean; error: boolean };

type ConfigurationProps = {
  euicc: string;
  appliedOtaConfigurationProfile?: string;
  appliedOtaConfigurationVersion?: string;
  isPendingConfiguration?: boolean;
  isCustomConfigurationDetected?: boolean;
};

type RSimInventoryConfigurationDialogProps<T extends ConfigurationProps> = {
  data: T;
  onClose: () => void;
  onRestoreStatusChange: (restoreStatus: RestoreStatuses) => void;
};

type ConfigurationDetailsRowProps = {
  title: string;
  value: string;
};

const ConfigurationDetailsRow: React.FC<ConfigurationDetailsRowProps> = ({ title, value }) => (
  <>
    <Grid
      size={7}
      sx={{
        mt: 8,
      }}
    >
      <Typography
        variant="text2"
        color="text.secondary"
        sx={{
          ml: 16,
        }}
      >
        {title}
      </Typography>
    </Grid>
    <Grid
      size={5}
      sx={{
        mt: 8,
      }}
    >
      <Typography
        sx={{
          whiteSpace: 'nowrap',
          display: 'flex',
          alignItems: 'center',
          ml: 28,
        }}
        variant="text1"
        color="text.primary"
      >
        {value || '-'}
      </Typography>
    </Grid>
  </>
);

export const RSimInventoryConfigurationDialogContent = <T extends ConfigurationProps>({
  data: configuration,
  onClose,
  onRestoreStatusChange,
}: RSimInventoryConfigurationDialogProps<T>) => {
  const { t } = useTranslation();

  const [selectedConfiguration, setSelectedConfiguration] =
    useState<PossibleConfigurationToRevert | null>(null);

  const ability = useAbility();

  const { data: possibleConfigurationsToRevert } = useSuspenseQuery({
    queryKey: ['possibleConfigurationsToRevert', configuration.euicc],
    queryFn: async ({ signal }) =>
      ability.can(Actions.restore, Subjects.otaConfiguration)
        ? fetchRSimPossibleOTAConfigurationsToRevert(configuration.euicc, signal)
        : null,
    initialData: ability.can(Actions.restore, Subjects.otaConfiguration) ? undefined : [],
  });

  const { data: appliedConfigurationDetails } = useSuspenseQuery({
    queryKey: ['appliedConfigurationDetails', configuration.euicc],
    queryFn: async ({ signal }) =>
      ability.can(Actions.read, Subjects.otaConfiguration)
        ? fetchRSimCurrentAppliedConfigurationDetails(configuration.euicc, signal)
        : null,
  });

  const { mutate: restoreConfiguration } = useMutation({
    mutationFn: async () => revertConfigurationVersion(selectedConfiguration!),
    onSuccess: async (res) => {
      onRestoreStatusChange({ success: res.status === 200, error: res.status !== 200 });
      setSelectedConfiguration(null);
      onClose();
    },
  });

  return (
    <Dialog
      open={true}
      title={
        selectedConfiguration
          ? t('rSimInventory.restoreConfiguration')
          : t('rSimInventory.configurationDetails')
      }
      subtitle={`rSIM ${configuration.euicc}`}
      onClose={onClose}
      size="small"
      actions={
        <>
          {!selectedConfiguration && (
            <>
              <SimInventoryCan I={Actions.restore} a={Subjects.otaConfiguration}>
                {possibleConfigurationsToRevert && possibleConfigurationsToRevert.length > 0 && (
                  <Select
                    name={t('rSimInventory.restoreConfiguration')}
                    displayEmpty
                    renderValue={
                      //@ts-expect-error -- trying to access selectedConfiguration but within the logic branch for !selectedConfiguration
                      selectedConfiguration && selectedConfiguration.rsimOtaProfileVersion !== ''
                        ? undefined
                        : () => t('rSimInventory.restoreConfiguration')
                    }
                    fullWidth={false}
                    sx={{ marginRight: 16, width: pxToRem(190) }}
                    onChange={(event) => {
                      if (possibleConfigurationsToRevert) {
                        //@ts-expect-error -- type for value is not given to select - event.target.value is unknown
                        const conf = possibleConfigurationsToRevert[+event.target.value];
                        if (conf) {
                          setSelectedConfiguration(conf);
                        } else {
                          setSelectedConfiguration(null);
                        }
                      }
                    }}
                  >
                    {possibleConfigurationsToRevert.map((d, i) => (
                      <MenuItem
                        key={`${d.rsimOtaProfileIdentifier} ${d.rsimOtaProfileVersion}`}
                        value={i}
                      >
                        {d.rsimOtaProfileIdentifier} {d.rsimOtaProfileVersion}
                      </MenuItem>
                    ))}
                  </Select>
                )}
              </SimInventoryCan>
              <Button variant="primary" onClick={onClose}>
                {t('common.close')}
              </Button>
            </>
          )}

          {selectedConfiguration && (
            <>
              <Button onClick={() => setSelectedConfiguration(null)} variant="secondary">
                {t('common.cancel')}
              </Button>

              <Button variant="primary" onClick={() => restoreConfiguration()}>
                {t('common.confirm')}
              </Button>
            </>
          )}
        </>
      }
    >
      <Stack direction="row" gap={220} alignItems="center" sx={{ background: 'red' }}>
        {!selectedConfiguration && (
          <>
            <Typography
              variant="text2"
              color="text.secondary"
              sx={{
                ml: 17,
              }}
            >
              {t('rSimInventory.configuration')}
            </Typography>

            <Box sx={{ whiteSpace: 'nowrap', display: 'flex', alignItems: 'center' }}>
              <Box
                sx={{
                  whiteSpace: 'nowrap',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-end',
                  width: pxToRem(34),
                }}
              >
                {configuration.isCustomConfigurationDetected && (
                  <Tooltip title={t('rSimInventory.customConfigurationDetected')}>
                    <ConfigurationIcon
                      width="1rem"
                      height="1rem"
                      role="img"
                      data-testid="custom-configuration-icon"
                    />
                  </Tooltip>
                )}
                {configuration.isPendingConfiguration && (
                  <Tooltip title={t('rSimInventory.configurationChangePending')}>
                    <PendingIcon
                      width="1.125rem"
                      height="1.125rem"
                      role="img"
                      data-testid="change-pending-icon"
                    />
                  </Tooltip>
                )}
              </Box>
              {configuration.appliedOtaConfigurationProfile && (
                <Typography
                  sx={{
                    ml: 4,
                  }}
                  variant="text1"
                  color="text.primary"
                >
                  {configuration.appliedOtaConfigurationProfile}
                </Typography>
              )}
              {!configuration.appliedOtaConfigurationProfile && (
                <Typography variant="text1" color="text.primary">
                  {t('rSimInventory.noConfigurationApplied')}
                </Typography>
              )}
              {configuration.appliedOtaConfigurationVersion && (
                <Typography marginLeft={8} variant="text5" color="text.secondary">
                  {configuration.appliedOtaConfigurationVersion || ''}
                </Typography>
              )}
            </Box>
          </>
        )}

        {selectedConfiguration && (
          <Typography variant="text1" color="text.primary">
            Are you sure you want to restore configuration{' '}
            <b>
              {' '}
              {selectedConfiguration?.rsimOtaProfileIdentifier}{' '}
              {selectedConfiguration?.rsimOtaProfileVersion}
            </b>
            ?
          </Typography>
        )}
      </Stack>

      {appliedConfigurationDetails && !selectedConfiguration && (
        <>
          <Grid
            container
            spacing={0}
            sx={{
              mt: 24,
            }}
          >
            <Grid>
              <Typography
                variant="h4"
                color="text.primary"
                sx={{
                  ml: 17,
                }}
              >
                {t('rSimInventory.currentlyAppliedConfiguration')}{' '}
                {configuration.isCustomConfigurationDetected && <>(custom)</>}
              </Typography>
            </Grid>
          </Grid>
          <Grid
            container
            spacing={0}
            sx={{ borderLeft: (theme) => `1px solid ${theme.palette.text.tertiary}` }}
          >
            <ConfigurationDetailsRow
              title={t('rSimInventory.connectionDelayEventCounter')}
              value={appliedConfigurationDetails.connectionDelayEventCounter}
            />
            <ConfigurationDetailsRow
              title={t('rSimInventory.requestedPoolInterval')}
              value={appliedConfigurationDetails.requestedPoolInterval}
            />
            <ConfigurationDetailsRow
              title={t('rSimInventory.commsCheckEventCounter')}
              value={appliedConfigurationDetails.commsCheckEventCounter}
            />
            <ConfigurationDetailsRow
              title={t('rSimInventory.maxConsecutiveFailedPoll')}
              value={appliedConfigurationDetails.maxConsecutiveFailedPoll}
            />
            <ConfigurationDetailsRow
              title={t('rSimInventory.backupCancellationDelayEventCounter')}
              value={appliedConfigurationDetails.backupCancellationDelayEventCounter}
            />
            <ConfigurationDetailsRow
              title={t('rSimInventory.maxGlobalSwapCounter')}
              value={appliedConfigurationDetails.maxGlobalSwapCounter}
            />
            <ConfigurationDetailsRow
              title={t('rSimInventory.resilienceRestorationDelayEventCounter')}
              value={appliedConfigurationDetails.resilienceRestorationDelayEventCounter}
            />
            <ConfigurationDetailsRow
              title={t('rSimInventory.maxRandConnectionDelayEventCounter')}
              value={appliedConfigurationDetails.maxRandConnectionDelayEventCounter}
            />
            <ConfigurationDetailsRow
              title={t('rSimInventory.maxRandBackupCancellationDelayEventCounter')}
              value={appliedConfigurationDetails.maxRandBackupCancellationDelayEventCounter}
            />
          </Grid>
        </>
      )}
    </Dialog>
  );
};

type RSimConfigurationDialogProps<T extends ConfigurationProps> = {
  data: T;
  open: boolean;
  onClose: () => void;
};

export const RSimConfigurationDialog = <T extends ConfigurationProps>({
  open,
  ...props
}: RSimConfigurationDialogProps<T>) => {
  const { t } = useTranslation();
  const [restoreStatus, setRestoreStatus] = useState<RestoreStatuses>({
    success: false,
    error: false,
  });

  return (
    <>
      {open ? (
        <RSimInventoryConfigurationDialogContent
          {...props}
          onRestoreStatusChange={setRestoreStatus}
        />
      ) : null}
      <Stack>
        <AutoHideSnackbar
          severity="success"
          open={restoreStatus.success}
          onClose={() => setRestoreStatus({ ...restoreStatus, success: false })}
        >
          <AlertTitle> {t('rSimInventory.restoreConfigurationSend')}</AlertTitle>
        </AutoHideSnackbar>
        <AutoHideSnackbar
          severity="error"
          open={restoreStatus.error}
          onClose={() => setRestoreStatus({ ...restoreStatus, error: false })}
        >
          <AlertTitle>{t('rSimInventory.restoreConfigurationFailed')}</AlertTitle>
        </AutoHideSnackbar>
      </Stack>
    </>
  );
};
